import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  styled,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  useTheme,
  FormHelperText,
  useMediaQuery,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton
} from "@mui/material";

// project imports
import AnimateButton from "../../../ui-component/extended/AnimateButton";
import useQuery from "../../../hooks/useQuery";
import { DocumentService } from "../../../services/intranet-portal-services";

// third party
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import { ErrorMessage, Form, Formik } from "formik";
import { toast } from "react-toastify";
import usePageTracking from "../../../utils/withPageTracking";

const MAX_UPLOAD_FILE_SIZE = 30 * 1024 * 1024;

interface PreviewFile extends File {
  preview: string;
}

const ReuploadDocument = (): React.ReactElement => {
  const navigate = useNavigate();
  const theme = useTheme<any>();
  const [files, setFiles] = useState<PreviewFile[]>([]);
  const [getEditDocumentData, setGetEditDocumentData] = useState<any>();
  const [addFileResponse, setAddFileResponse] = useState<any>();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const params = useQuery();
  const id: any = params.get("id");

  const initialState = {
    documentId: id,
    documentUrl: getEditDocumentData?.documentUrl ?? ""
  };

  usePageTracking();
  const handleDocumentApp = async (val: any): Promise<void> => {
    const formData: any = {
      documentId: id,
      documentUrl: addFileResponse
    };
    try {
      const addresponse: any =
        await DocumentService.postCoreApiApiAppDocumentReuploadDocument(formData);
      if (addresponse != null) {
        toast.success("Document reuploaded successfully");
        void navigate(-1);
      }
    } catch (e: any) {
      if (e.status != null) {
        toast.error(e.status);
      } else if (e?.body?.error?.validationErrors != null) {
        toast.error(e?.body?.error?.validationErrors);
      } else if (e.message != null) {
        toast.error(e.message);
      } else {
        toast.error("Unable to fetch api");
      }
    }
  };

  const fetchEditData = async (): Promise<void> => {
    try {
      const editResponse = await DocumentService.getCoreApiApiAppDocumentDocumentById(id);
      setGetEditDocumentData(editResponse);
    } catch (e: any) {
      if (e.status != null) {
        toast.error(e.status);
      } else if (e?.body?.error?.validationErrors != null) {
        toast.error(e?.body?.error?.validationErrors);
      } else if (e.message != null) {
        toast.error(e.message);
      } else {
        toast.error("Unable to fetch api");
      }
    }
  };

  useEffect(() => {
    if (id != null) {
      void fetchEditData();
    }
  }, []);

  const handleDocumentLogo = async (val: File): Promise<void> => {
    try {
      const formDataFile = {
        file: val
      };
      const addFileResponse =
        await DocumentService.postCoreApiApiAppDocumentUploadDocument(formDataFile);
      if (addFileResponse != null) {
        setAddFileResponse(addFileResponse);
        toast.success("File uploaded successfully");
      }
    } catch (e: any) {
      if (e.status != null) {
        toast.error(e.status);
      } else if (e?.body?.error?.validationErrors != null) {
        toast.error(e?.body?.error?.validationErrors);
      } else if (e.message != null) {
        toast.error(e.message);
      } else {
        toast.error("Unable to fetch api");
      }
    }
  };

  const validateSelectedFile = (selectedFile: File): boolean => {
    const allowedTypes = ["application/pdf", "image/png"];
    const isAllowedType = allowedTypes.includes(selectedFile.type);
    const isAllowedSize = selectedFile.size <= MAX_UPLOAD_FILE_SIZE;

    if (!isAllowedType || !isAllowedSize) {
      toast.error("Only PDF or PNG files up to 30MB are supported");
      return false;
    }

    return true;
  };

  const handleSelectedFile = (selectedFile: File): void => {
    if (!validateSelectedFile(selectedFile)) {
      return;
    }

    setFiles([
      Object.assign(selectedFile, {
        preview: URL.createObjectURL(selectedFile)
      }) as PreviewFile
    ]);

    void handleDocumentLogo(selectedFile);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile == null) {
      return;
    }

    handleSelectedFile(selectedFile);
    event.target.value = "";
  };

  const handleDropzoneClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const selectedFile = event.dataTransfer.files[0];

    if (selectedFile == null) {
      return;
    }

    handleSelectedFile(selectedFile);
  };

  const removeFile = (name: string): void => {
    setFiles((currentFiles) => {
      const fileToRemove = currentFiles.find((file) => file.name === name);

      if (fileToRemove != null) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      return currentFiles.filter((file) => file.name !== name);
    });
    setAddFileResponse(undefined);
  };

  const Dropzone = styled("div")({
    flex: "1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: "2px",
    borderRadius: "2px",
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
    "&:hover": {
      borderColor: "#c62828"
    }
  });

  return (
    <>
      <Box>
        <Grid container spacing={2} mb={2} alignItems={"center"}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={{ fontWeight: 500, color: "#D42C2C" }}>
              Reupload Document
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: 3 }}>
        <Card>
          <CardContent>
            {/* Logo Upload Area of document app */}
            <Box>
              <Dropzone
                onClick={handleDropzoneClick}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                aria-label="Select file"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png"
                  onChange={handleFileInputChange}
                  style={{ display: "none" }}
                />
                <p>click to select files</p>
              </Dropzone>
              <Box>
                <>
                  <ImageList cols={8}>
                    {files?.map((file: PreviewFile) => (
                      <ImageListItem key={file.name}>
                        <img
                          src={file.preview}
                          alt={file.name}
                          width={100}
                          height={100}
                          onLoad={() => {
                            URL.revokeObjectURL(file.preview);
                          }}
                          loading="lazy"
                        />
                        <ImageListItemBar
                          sx={{
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.7) 0%, " +
                              "rgba(0,0,0,0.6) 70%, rgba(0,0,0,0) 100%)"
                          }}
                          position="bottom"
                          actionIcon={
                            <IconButton sx={{ color: "white" }}>
                              <HighlightOffRoundedIcon sx={{ fontSize: "24px" }} />
                            </IconButton>
                          }
                          actionPosition="right"
                          onClick={() => {
                            removeFile(file.name);
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </>
              </Box>
            </Box>
            {/* Form post of internal app */}
            <Formik
              initialValues={
                id != null
                  ? {
                      documentUrl: getEditDocumentData?.documentUrl ?? ""
                    }
                  : initialState
              }
              onSubmit={handleDocumentApp}
              enableReinitialize={id}
            >
              {({ handleChange }) => (
                <Form noValidate>
                  <Grid container spacing={matchDownSM ? 0 : 2} marginY={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={addFileResponse == null ? "" : "Document Url"}
                        margin="none"
                        name="documentUrl"
                        type="text"
                        value={addFileResponse}
                        onChange={handleChange}
                        autoComplete="off"
                        variant="outlined"
                        disabled
                      />
                      <FormHelperText error id="standard-weight-helper-text--register">
                        <ErrorMessage name="documentUrl" />
                      </FormHelperText>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2 }}>
                    <AnimateButton>
                      <Button
                        disableElevation
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="error"
                      >
                        Reupload
                      </Button>
                    </AnimateButton>
                  </Box>
                </Form>
              )}
            </Formik>
            <AnimateButton>
              <Button
                sx={{ mt: 2 }}
                disableElevation
                fullWidth
                size="large"
                type="submit"
                variant="outlined"
                color="error"
                onClick={() => {
                  void navigate(-1);
                }}
              >
                Back To the list
              </Button>
            </AnimateButton>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};
export default ReuploadDocument;
